import { draftAsContent } from '@/lib/experience-draft';
import { countLabel, pageWhere } from '@/lib/experience-labels';
import type { VariableInk, VariableSources } from '@/lib/variable-pill';
import type { DeclaredVariable, VariablePlace } from '@matter_extractor/shared';
import {
  NOYAU_TOKEN,
  experienceVariableToken,
  firstChoiceOf,
  variableReach,
} from '@matter_extractor/shared';

/**
 * Ce que la palette `@` offre à l'endroit où l'on écrit, et RIEN de plus
 * (refonte modulaire §8.6, H61 ; grill Variables du 2026-09-16, D3 ; maquette
 * `variables.html`, planche 1 ; issue #88).
 *
 * « La palette ne propose que ce qui est en portée à l'endroit où l'on écrit,
 * et compte en pied ce qu'elle ne propose pas. » The reach is the pure rule of
 * `shared` (`variableReach`), the very one the save refuses on
 * (`variable-out-of-scope`): what this palette offers and what the save admits
 * are one list by construction, which is the whole point of D3.
 *
 * ## Four groups, by provenance and by position
 *
 * « Champs des pages précédentes », « Choix des pages précédentes »,
 * « Objectifs du protocole », « Critères », in that order (mockup README). A
 * group with nothing to offer is left out, and the FOOT says what was left out
 * on purpose: a field declared after this page, an objective before the
 * protocol page. An absence with no sentence would send an operator looking for
 * a variable they created a minute ago (H61).
 *
 * ## The criteria are the protocol's
 *
 * A criterion is named by its own entity and not by the protocol that cites it,
 * so the list is the protocol's citations resolved against the library the
 * screen read. An unread library offers no criterion rather than a nameless
 * one - the régime `protocol-editor.tsx` holds with `criteriaRead`.
 *
 * Filtered by what has been typed after the `@`, accent-blind and case-blind,
 * because a palette that missed « prenom » for « prénom » would be a palette
 * one has to spell into.
 */

/** One line of the menu: the token to insert, and what tells it apart. */
export type PaletteEntry = {
  readonly token: string;
  readonly label: string;
  /** « page 2, Un mot d'accueil » or « étape 1 · obligatoire » - what a homonym is told by. */
  readonly meta: string;
  readonly ink: VariableInk;
  /** Two objectives share this name: the meta is inked, as the protocol's palette does. */
  readonly duplicate: boolean;
};

export type PaletteGroup = {
  readonly title: string;
  readonly entries: readonly PaletteEntry[];
};

/** The menu as a whole: its groups, and the foot that counts what is not offered (H61). */
export type PaletteContent = {
  readonly groups: readonly PaletteGroup[];
  readonly footer: readonly string[];
};

export const PALETTE_FIELDS = 'Champs des pages précédentes';
export const PALETTE_CHOICES = 'Choix des pages précédentes';
/** `@noyau`, the retained noyau (noyau.md §3.3) - one line, from the page after the first choice. */
export const PALETTE_NOYAU = 'Noyau';
export const PALETTE_OBJECTIVES = 'Objectifs du protocole';
export const PALETTE_CRITERIA = 'Critères';

/**
 * Case and accents folded so « prenom » finds « prénom ». A code-point walk and
 * not a character class, for the reason `ambiance-assets.ts` spells out at its
 * own `fold`: a class spanning the combining marks is what
 * `noMisleadingCharacterClass` refuses, and it is right to - the range is only
 * safe BECAUSE the string was decomposed first, which the class cannot say.
 */
export function foldForSearch(text: string): string {
  let folded = '';
  for (const character of text.toLowerCase().normalize('NFD')) {
    const code = character.codePointAt(0) ?? 0;
    if (code >= 0x300 && code <= 0x36f) continue;
    folded += character;
  }
  return folded;
}

/**
 * « 1 variable saisie après cette page n'est pas proposée : email, page 4. »
 *
 * Plural agreed by hand - « 2 variables saisies après cette page ne sont pas
 * proposées : prénom, page 1 · email, page 4. » - and null when everything
 * declared is offered, so the foot takes no room on the common case.
 *
 * ## « après cette page » ne couvre PAS ce que la règle retire
 *
 * The reach cuts at `servedAt` INCLUSIVE (`pageIndex >= servedAt`), so the foot
 * of the first page lists the fields of the first page - typed by the person
 * after the text is rendered, hence unusable in it. Under one wording, that
 * foot read « 3 variables saisies après cette page … : prénom, page 1 · email,
 * page 1 · consentement, page 1 », which names page 1 three times to somebody
 * standing on page 1 - the sentence contradicting its own list. The tail is
 * therefore read off the list, and a same-page variable drops the « , page N »
 * that says nothing where it is already said.
 */
export function outOfReachSentence(
  outOfReach: readonly DeclaredVariable[],
  servedAt: number,
): string | null {
  if (outOfReach.length === 0) return null;
  const several = outOfReach.length > 1;
  const here = outOfReach.some((variable) => variable.pageIndex === servedAt);
  const later = outOfReach.some((variable) => variable.pageIndex > servedAt);
  const where = here ? (later ? 'sur cette page ou après' : 'sur cette page') : 'après cette page';
  const named = outOfReach
    .map((variable) =>
      variable.pageIndex === servedAt
        ? variable.name
        : `${variable.name}, ${pageWhere(variable.pageIndex, null)}`,
    )
    .join(' · ');
  return `${countLabel(outOfReach.length, 'variable')} ${several ? 'saisies' : 'saisie'} ${where} ${several ? 'ne sont pas proposées' : 'n’est pas proposée'} : ${named}.`;
}

/** The second foot line, when the protocol's objectives exist and cannot resolve here. */
export const OBJECTIVES_OUT_OF_REACH =
  'Les objectifs du protocole n’ont de matière qu’après la page protocole : ils ne sont pas proposés ici.';

/** The foot line of `@noyau`, when a first choice exists and gives it no value here (§3.3). */
export const NOYAU_OUT_OF_REACH =
  'Le noyau retenu n’existe qu’à partir de la page qui suit le premier choix : il n’est pas proposé ici.';

/**
 * What the menu of a bounded field says above its lines once a token no longer
 * fits where it would land (#160) - the directions' palette says the reading's
 * own, `READING_PALETTE_FULL`. A line greyed without a word reads as a broken
 * variable, not as a full text.
 */
export function paletteFullSentence(max: number): string {
  return `Plus de place pour une variable : le champ dépasserait ses ${max} caractères. Raccourcir le texte pour en insérer une.`;
}

export function variablePalette(
  sources: VariableSources,
  place: VariablePlace,
  query: string,
): PaletteContent {
  const wanted = foldForSearch(query);
  const keep = (entries: PaletteEntry[]): PaletteEntry[] =>
    wanted === ''
      ? entries
      : entries.filter((entry) => foldForSearch(entry.label).includes(wanted));

  const reach = variableReach(draftAsContent(sources.draft), place);
  const timeline = (variable: DeclaredVariable): PaletteEntry => ({
    token: experienceVariableToken({ kind: variable.kind, id: variable.id }),
    label: variable.name,
    meta: pageWhere(variable.pageIndex, variable.pageName),
    ink: 'value',
    duplicate: false,
  });
  const fields = reach.variables.filter((one) => one.kind === 'field').map(timeline);
  const choices = reach.variables.filter((one) => one.kind === 'choice').map(timeline);

  const protocol = sources.protocol;
  const objectives: PaletteEntry[] = [];
  const criterionIds = new Set<string>();
  if (protocol !== null) {
    const counts = new Map<string, number>();
    for (const step of protocol.content.steps) {
      for (const objective of step.objectives) {
        const key = foldForSearch(objective.name.trim());
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    protocol.content.steps.forEach((step, stepIndex) => {
      for (const objective of step.objectives) {
        objectives.push({
          token: experienceVariableToken({ kind: 'objective', id: objective.id }),
          label: objective.name,
          meta: `étape ${stepIndex + 1} · ${objective.mandatory ? 'obligatoire' : 'optionnel'}`,
          ink: 'objective',
          duplicate: (counts.get(foldForSearch(objective.name.trim())) ?? 0) > 1,
        });
        for (const requirement of objective.criteria) criterionIds.add(requirement.ref.id);
      }
    });
  }
  const criteria: PaletteEntry[] = sources.criteriaRead
    ? sources.criteria
        .filter((card) => criterionIds.has(card.id) && card.retiredAt === null)
        .map((card) => ({
          token: experienceVariableToken({ kind: 'criterion', id: card.id }),
          label: card.name,
          meta: 'critère du protocole',
          ink: 'criterion',
          duplicate: false,
        }))
    : [];

  // `@noyau` (noyau.md §3.3, #151): ONE line, given by the first choice, in
  // reach from the page that follows it - the rule the save refuses on.
  const first = firstChoiceOf(draftAsContent(sources.draft));
  const noyau: PaletteEntry[] =
    first === null
      ? []
      : [
          {
            token: NOYAU_TOKEN,
            label: 'noyau',
            meta: `premier choix, ${pageWhere(first.pageIndex, first.pageName)}`,
            ink: 'noyau',
            duplicate: false,
          },
        ];

  const groups = [
    { title: PALETTE_FIELDS, entries: keep(fields) },
    { title: PALETTE_CHOICES, entries: keep(choices) },
    { title: PALETTE_NOYAU, entries: reach.noyau ? keep(noyau) : [] },
    { title: PALETTE_OBJECTIVES, entries: reach.objectives ? keep(objectives) : [] },
    { title: PALETTE_CRITERIA, entries: keep(criteria) },
  ].filter((group) => group.entries.length > 0);

  const footer: string[] = [];
  const hidden = outOfReachSentence(reach.outOfReach, reach.servedAt);
  if (hidden !== null) footer.push(hidden);
  if (!reach.noyau && noyau.length > 0) footer.push(NOYAU_OUT_OF_REACH);
  if (!reach.objectives && objectives.length > 0) footer.push(OBJECTIVES_OUT_OF_REACH);

  return { groups, footer };
}

/** What Entrée inserts while the menu is open: the first line of the first group. */
export function firstPaletteToken(content: PaletteContent): string | null {
  for (const group of content.groups) {
    const first = group.entries[0];
    if (first !== undefined) return first.token;
  }
  return null;
}
